import React, { useState, useEffect, useCallback } from 'react';
import { FaPaperPlane, FaSync, FaExclamationCircle, FaFilter, FaTimes, FaBan } from 'react-icons/fa';
import { formatDate } from '../utils/dateUtils';
import XeroStatusBadge from './XeroStatusBadge';
import { searchFranchisees } from '../services/franchiseeService';
import { searchHouseholds } from '../services/householdService';
import { debounce } from 'lodash';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'awaiting_approval', label: 'Awaiting Approval' },
  { value: 'awaiting_payment', label: 'Awaiting Payment' },
  { value: 'paid', label: 'Paid' },
  { value: 'void', label: 'Void' },
];

const XERO_STATUS_OPTIONS = [
  { value: '', label: 'All Xero Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'AUTHORISED', label: 'Authorised' },
  { value: 'PAID', label: 'Paid' },
  { value: 'VOIDED', label: 'Voided' },
];

// No 'Not imported' option: native invoices have no 'imported' field, so a Firestore equality on false
// can never match them (it would render an empty list that looks like data loss). 'All Sources' already
// shows the non-imported set.
const IMPORTED_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'true', label: 'Imported only' },
];

const calculateAmountDue = (invoice) => {
  if (invoice.Quantity == null || invoice.UnitAmount == null) return 'N/A';
  const amount = invoice.Quantity * invoice.UnitAmount;
  return `$${amount.toFixed(2)}`;
};

// Reusable debounced search helper (mirrors JobSchedulerPage)
const useSearch = (searchFunction, options = {}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const optionsString = JSON.stringify(options);

  const debouncedFetch = useCallback(
    debounce(async (term, fetchOptions) => {
      if (!searchFunction || term.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      setIsSearching(true);
      try {
        const data = await searchFunction({ searchString: term, ...fetchOptions });
        const results = data.franchisees || data.clients || data.households || [];
        setSuggestions(results);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [searchFunction]
  );

  useEffect(() => {
    const currentOptions = JSON.parse(optionsString);
    debouncedFetch(searchTerm, currentOptions);
    return () => debouncedFetch.cancel();
  }, [searchTerm, optionsString, debouncedFetch]);

  return { searchTerm, setSearchTerm, suggestions, setSuggestions, isSearching, showSuggestions, setShowSuggestions };
};

const formatHouseholdLabel = (household) => {
  if (!household) return 'N/A';
  const name = `${household.firstName || ''} ${household.lastName || ''}`.trim() || household.householdName || 'N/A';
  const address = household.addressLine1 ? ` (${household.addressLine1})` : '';
  return `${name}${address}`;
};

const InvoiceTabContent = ({
  invoices,
  loading,
  error,
  syncingIds,
  selectedInvoices,
  selectedStatus,
  sendingToXero,
  isSyncing,
  isSendToXeroDisabled,
  draftCount,
  selectableDraftsCount,
  allDraftSelected,
  onStatusChange,
  onSelectInvoice,
  onSelectAllDraft,
  onSendToXero,
  onSync,
  onRowClick,
  onXeroStatusClick,
  onCompletionDateClick,
  onVoidInvoice,
  voidingInvoiceId,
  currentPage,
  pageSize,
  hasNextPage,
  onPageChange,
  onPageSizeChange,
  isNavigating,
  filterFseId,
  filterHouseholdId,
  filterXeroStatus,
  filterInvoiceNumber,
  filterImported,
  sortDirection,
  onFilterChange,
  onClearFilters,
  onSortDirectionChange,
  duplicatesCount,
  resolvedDuplicatesCount = 0,
  onCheckDuplicates,
  onViewDuplicates,
  checkingDuplicates
}) => {
  const franchiseeSearch = useSearch(searchFranchisees);
  const householdSearch = useSearch(searchHouseholds);

  // Local state for display values (separate from search terms)
  const [fseDisplayValue, setFseDisplayValue] = useState(filterFseId || '');
  const [householdDisplayValue, setHouseholdDisplayValue] = useState(filterHouseholdId ? `Client ${filterHouseholdId.substring(0,6)}...` : '');


  useEffect(() => {
    if (filterFseId) {
      franchiseeSearch.setShowSuggestions(false);
    }
    if (filterHouseholdId) {
      householdSearch.setShowSuggestions(false);
    }
  }, [filterFseId, filterHouseholdId, franchiseeSearch, householdSearch]);

  const handleFseInputChange = (value) => {
    setFseDisplayValue(value);
    franchiseeSearch.setSearchTerm(value);
    onFilterChange('fseCode', null);
    if (value.length < 3) {
      franchiseeSearch.setSuggestions([]);
      franchiseeSearch.setShowSuggestions(false);
    }
  };

  const handleHouseholdInputChange = (value) => {
    setHouseholdDisplayValue(value);
    householdSearch.setSearchTerm(value);
    onFilterChange('householdId', null);
    if (value.length < 3) {
      householdSearch.setSuggestions([]);
      householdSearch.setShowSuggestions(false);
    }
  };

  const handleFseSelect = (fse) => {
    const contactName = `${fse.firstName || ''} ${fse.lastName || ''}`.trim();
    const displayName = `${fse.franchiseName || 'Unknown Franchise'}${contactName ? ` - ${contactName}` : ''}`;
    setFseDisplayValue(displayName);
    // DON'T set searchTerm here to avoid triggering another search
    franchiseeSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    onFilterChange('fseCode', fse.fseCode, { autoApply: true });
  };

  const handleHouseholdSelect = (household) => {
    const displayName = formatHouseholdLabel(household);
    setHouseholdDisplayValue(displayName);
    // DON'T set searchTerm here to avoid triggering another search
    householdSearch.setSuggestions([]);
    householdSearch.setShowSuggestions(false);
    onFilterChange('householdId', household.id, { autoApply: true });
  };

  const handleApplyFilters = () => {
    onFilterChange('apply');
  };

  const handleClearFiltersLocal = () => {
    setFseDisplayValue('');
    setHouseholdDisplayValue('');
    franchiseeSearch.setSearchTerm('');
    householdSearch.setSearchTerm('');
    franchiseeSearch.setSuggestions([]);
    householdSearch.setSuggestions([]);
    franchiseeSearch.setShowSuggestions(false);
    householdSearch.setShowSuggestions(false);
    onClearFilters();
  };

  const renderErrorMessage = (message) => {
    if (!message) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = String(message).split(urlRegex);
    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={`error-link-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline text-red-800 break-all"
          >
            {part}
          </a>
        );
      }
      return <span key={`error-text-${index}`}>{part}</span>;
    });
  };

  const getStatusPillProps = (status) => {
    const normalized = status || '';
    const pillClass = normalized === 'deleted' || normalized === 'void'
      ? 'bg-red-100 text-red-800'
      : normalized === 'awaiting_payment'
      ? 'bg-orange-100 text-orange-600'
      : normalized === 'paid'
      ? 'bg-green-100 text-green-800'
      : normalized === 'draft'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-gray-100 text-gray-800';

    const label = normalized
      ? normalized.charAt(0).toUpperCase() + normalized.slice(1).replace('_', ' ')
      : 'N/A';

    return { pillClass, label };
  };

  return (
    <div>
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded break-words whitespace-normal">
          {renderErrorMessage(error)}
        </div>
      )}

      {/* Filters Row */}
      <div className="mb-4">
        {/* Row 1: Searchable filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          {/* Franchisee Filter */}
          <div className="relative">
            <label htmlFor="fseFilter" className="block text-xs font-medium text-gray-600 mb-1">Filter by Franchisee</label>
            <input
              id="fseFilter"
              type="text"
              autoComplete="off"
              value={fseDisplayValue}
              onChange={(e) => handleFseInputChange(e.target.value)}
              onBlur={() => setTimeout(() => franchiseeSearch.setShowSuggestions(false), 150)}
              onFocus={() => franchiseeSearch.suggestions.length > 0 && franchiseeSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            />
            {franchiseeSearch.showSuggestions && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {franchiseeSearch.isSearching ? (
                  <li className="px-3 py-2 text-gray-500 text-sm">Searching...</li>
                ) : franchiseeSearch.suggestions.length > 0 ? (
                  franchiseeSearch.suggestions.map(fse => (
                    <li
                      key={fse.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onMouseDown={() => handleFseSelect(fse)}
                    >
                      <div className="font-medium">{fse.firstName} {fse.lastName}</div>
                      <div className="text-xs text-gray-500">{fse.franchiseName || fse.fseCode}</div>
                    </li>
                  ))
                ) : fseDisplayValue.length >= 3 && (
                  <li className="px-3 py-2 text-gray-500 text-sm">No franchisees found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Household (Client) Filter */}
          <div className="relative">
            <label htmlFor="clientFilter" className="block text-xs font-medium text-gray-600 mb-1">Filter by Client</label>
            <input
              id="clientFilter"
              type="text"
              autoComplete="off"
              value={householdDisplayValue}
              onChange={(e) => handleHouseholdInputChange(e.target.value)}
              onBlur={() => setTimeout(() => householdSearch.setShowSuggestions(false), 150)}
              onFocus={() => householdSearch.suggestions.length > 0 && householdSearch.setShowSuggestions(true)}
              placeholder="Type to search..."
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            />
            {householdSearch.showSuggestions && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                {householdSearch.isSearching ? (
                  <li className="px-3 py-2 text-gray-500 text-sm">Searching...</li>
                ) : householdSearch.suggestions.length > 0 ? (
                  householdSearch.suggestions.map(household => {
                    const label = formatHouseholdLabel(household);
                    return (
                    <li
                      key={household.id}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      onMouseDown={() => handleHouseholdSelect(household)}
                    >
                      <div className="font-medium">{label}</div>
                      {household.email && <div className="text-xs text-gray-500">{household.email}</div>}
                    </li>
                    );
                  })
                ) : householdDisplayValue.length >= 3 && (
                  <li className="px-3 py-2 text-gray-500 text-sm">No clients found.</li>
                )}
              </ul>
            )}
          </div>

          {/* Invoice Number Filter */}
          <div>
            <label htmlFor="invoiceNumberFilter" className="block text-xs font-medium text-gray-600 mb-1">
              Invoice Number
            </label>
            <input
              id="invoiceNumberFilter"
              type="text"
              value={filterInvoiceNumber || ''}
              onChange={(e) => onFilterChange('invoiceNumber', e.target.value || null)}
              onKeyDown={(e) => e.key === 'Enter' && onFilterChange('apply')}
              placeholder="e.g. INV-1234"
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            />
          </div>
        </div>

        {/* Row 2: Selectable filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3">

          {/* Loope Status Filter */}
          <div>
            <label htmlFor="statusFilter" className="block text-xs font-medium text-gray-600 mb-1">
              Loope Status
            </label>
            <select
              id="statusFilter"
              value={selectedStatus}
              onChange={onStatusChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            >
              {STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Xero Status Filter */}
          <div>
            <label htmlFor="xeroStatusFilter" className="block text-xs font-medium text-gray-600 mb-1">
              Xero Status
            </label>
            <select
              id="xeroStatusFilter"
              value={filterXeroStatus || ''}
              onChange={(e) => onFilterChange('xeroStatus', e.target.value || null, { autoApply: true })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            >
              {XERO_STATUS_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort by Date */}
          <div>
            <label htmlFor="sortDirection" className="block text-xs font-medium text-gray-600 mb-1">Sort by Date</label>
            <select
              id="sortDirection"
              value={sortDirection || 'desc'}
              onChange={(e) => onSortDirectionChange(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          {/* Imported Filter */}
          <div>
            <label htmlFor="importedFilter" className="block text-xs font-medium text-gray-600 mb-1">
              Source
            </label>
            <select
              id="importedFilter"
              value={filterImported ?? ''}
              onChange={(e) => onFilterChange('imported', e.target.value || null, { autoApply: true })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
            >
              {IMPORTED_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Action Buttons */}
        <div className="flex justify-end space-x-2 mt-3 flex-wrap">
          <button
            onClick={handleClearFiltersLocal}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 inline-flex items-center"
            disabled={loading}
          >
            <FaTimes className="mr-1" size={12} /> Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-3 py-1.5 text-sm font-medium text-white bg-jims-cyan rounded-md hover:bg-jims-cyan-dark inline-flex items-center"
            disabled={loading || franchiseeSearch.isSearching || householdSearch.isSearching}
          >
            <FaFilter className="mr-1" size={12} /> Apply
          </button>
          <button
            onClick={onCheckDuplicates}
            disabled={checkingDuplicates || loading}
            className={`px-3 py-1.5 text-sm font-medium rounded-md inline-flex items-center ${
              checkingDuplicates || loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-yellow-600 text-white hover:bg-yellow-700'
            }`}
          >
            {checkingDuplicates ? (
              <>
                <FaSync className="animate-spin mr-1" size={12} />
                Checking...
              </>
            ) : (
              <>
                <FaExclamationCircle className="mr-1" size={12} />
                Check for Duplicates
              </>
            )}
          </button>
        </div>

        {/* Duplicate Notification Banner */}
        {duplicatesCount > 0 && (
          <div className="mt-3 p-3 bg-red-50 border border-red-300 rounded-md cursor-pointer hover:bg-red-100 transition-colors" onClick={onViewDuplicates}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FaExclamationCircle className="text-red-600 mr-2" size={16} />
                <span className="text-sm font-medium text-red-800">
                  Duplicate invoices detected. Click here to view the list ({duplicatesCount})
                </span>
              </div>
            </div>
          </div>
        )}

        {duplicatesCount === 0 && resolvedDuplicatesCount > 0 && (
          <div className="mt-3 p-3 bg-gray-50 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 transition-colors" onClick={onViewDuplicates}>
            <div className="flex items-center">
              <FaExclamationCircle className="text-gray-500 mr-2" size={16} />
              <span className="text-sm font-medium text-gray-700">
                No active duplicate invoices. View dismissed duplicates ({resolvedDuplicatesCount})
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mb-6 flex items-center justify-start space-x-3">
        {draftCount > 0 && (
          <>
            <button
              onClick={onSelectAllDraft}
              disabled={selectableDraftsCount === 0}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                selectableDraftsCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : allDraftSelected
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              } transition duration-300`}
            >
              {allDraftSelected ? 'Deselect All Draft' : `Select All Draft (${selectableDraftsCount})`}
            </button>

            <button
              onClick={onSendToXero}
              disabled={isSendToXeroDisabled || sendingToXero}
              className={`px-4 py-2 text-sm font-medium rounded-md inline-flex items-center ${
                isSendToXeroDisabled || sendingToXero
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-jims-green text-white hover:bg-jims-green-dark'
              } transition duration-300`}
            >
              {sendingToXero ? (
                <>
                  <FaSync className="animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                <>
                  <FaPaperPlane className="mr-2" />
                  Send to Xero ({selectedInvoices.size})
                </>
              )}
            </button>
          </>
        )}

        <button
          onClick={onSync}
          disabled={isSyncing}
          className={`px-4 py-2 text-sm font-medium rounded-md inline-flex items-center ${
            isSyncing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-jims-cyan text-white hover:bg-jims-cyan-dark'
          } transition duration-300`}
        >
          {isSyncing ? (
            <>
              <FaSync className="animate-spin mr-2" />
              Syncing...
            </>
          ) : (
            <>
              <FaSync className="mr-2" />
              Sync with Xero
            </>
          )}
        </button>
      </div>

      {/* Invoice Table */}
      <div className="bg-white shadow overflow-hidden rounded-lg">
        {loading ? (
          <p className="text-center text-gray-500 py-10">Loading invoices...</p>
        ) : invoices.length === 0 ? (
          <div className="text-center text-gray-500 p-6">
            <p>No invoices found{selectedStatus ? ` with status "${selectedStatus.replace('_', ' ')}"` : ''}.</p>
          </div>
        ) : (
          <table className="min-w-full leading-normal">
            <thead>
              <tr className="bg-gray-200 text-left text-gray-600 uppercase text-sm">
                <th className="px-5 py-3 border-b-2 border-gray-200">Select</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Invoice #</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Franchisee</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Client</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Service Completion Date</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Amount Due</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Loope Status</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">Invoice Xero</th>
                <th className="px-5 py-3 border-b-2 border-gray-200">RCTI Status</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const isDraftSelectable = invoice.status === 'draft' && invoice.xero_submission_status !== 'success';
                
                return (
                  <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onRowClick(invoice.id)}>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      {isDraftSelectable ? (
                        <input
                          type="checkbox"
                          checked={selectedInvoices.has(invoice.id)}
                          onChange={() => onSelectInvoice(invoice.id, invoice.status)}
                          className="h-4 w-4 text-jims-cyan focus:ring-jims-cyan border-gray-300 rounded"
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{invoice.InvoiceNumber}</p>
                      <p className="text-gray-500 whitespace-no-wrap text-xs">{formatDate(invoice.createdAt)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{invoice.TrackingOption1 || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap">{invoice.ContactName || 'N/A'}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      <div>
                        <p className="text-gray-900 whitespace-no-wrap">
                          {invoice.completionDate ? formatDate(invoice.completionDate) : <span className="text-gray-400">-</span>}
                        </p>
                        {invoice.jobId && (
                          <button
                            type="button"
                            className="text-xs text-jims-cyan hover:text-jims-cyan-dark hover:underline cursor-pointer"
                            onClick={() => onCompletionDateClick && onCompletionDateClick(invoice.jobId)}
                            title="View job details"
                          >
                            View job
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm">
                      <p className="text-gray-900 whitespace-no-wrap font-medium">{calculateAmountDue(invoice)}</p>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {(() => {
                          const { pillClass, label } = getStatusPillProps(invoice.status);
                          return <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${pillClass}`}>{label}</span>;
                        })()}
                        {invoice.imported === true && (
                          <span className="px-2 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            Imported
                          </span>
                        )}
                        {/* Void action moved to Edit Job page */}
                      </div>
                    </td>
                    <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      <XeroStatusBadge
                        submissionStatus={invoice.xero_submission_status}
                        invoiceStatus={invoice.xero_invoice_status}
                        isSyncing={syncingIds?.has(invoice.id)}
                        onClick={() => onXeroStatusClick(invoice)}
                      />
                    </td>
                     <td className="px-5 py-4 border-b border-gray-200 bg-white text-sm" onClick={(e) => e.stopPropagation()}>
                      {invoice.relatedRCTI ? (
                        (() => {
                          const relatedStatus = invoice.relatedRCTI.status
                          const { pillClass, label } = getStatusPillProps(relatedStatus);
                          const displayLabel = relatedStatus === 'notified' ? 'FSE Notified' : label;
                          const content = <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${pillClass}`}>{displayLabel}</span>;
                          return (
                            <button
                              type="button"
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/rctis/${invoice.relatedRCTI.id}?returnTab=invoices`, '_blank');
                              }}
                              title={
                                invoice.relatedRCTI?.RCTINumber
                                  ? `RCTI #${invoice.relatedRCTI.RCTINumber}`
                                  : 'View RCTI'
                              }
                            >
                              {content}
                            </button>
                          );
                        })()
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        
        {/* Pagination Controls */}
        {!loading && invoices.length > 0 && (
          <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              {/* Left side - Page size selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Items per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-jims-cyan focus:border-jims-cyan text-sm"
                >
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={40}>40</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">
                  {invoices.length < pageSize ? `Showing ${invoices.length}` : `Showing ${pageSize}`}
                </span>
              </div>

              {/* Right side - Page navigation */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md inline-flex items-center ${
                    currentPage === 1 || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating ? (
                    <>
                      <FaSync className="animate-spin mr-1" size={12} />
                      Previous
                    </>
                  ) : (
                    'Previous'
                  )}
                </button>
                
                <span className="text-sm text-gray-700 px-2">
                  Page {currentPage}
                </span>
                
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={!hasNextPage || isNavigating}
                  className={`px-3 py-1 text-sm font-medium rounded-md inline-flex items-center ${
                    !hasNextPage || isNavigating
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {isNavigating ? (
                    <>
                      <FaSync className="animate-spin mr-1" size={12} />
                      Next
                    </>
                  ) : (
                    'Next'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default InvoiceTabContent;
